import config from "../lib/config";
import {
  GetUserGrantsProps,
  MFAEnrolledFactorAPI,
  MFAAvailableFactorAPI,
  ChangePasswordResponse,
  UpdateMfaPromptResponse,
} from "../types/ApiResponse.type";
import callApi from "./CallApi";
import parse from "parse-link-header";
import {
  GET_GRANTS_API_LIMIT,
  NETWORK_RETRY_ERRORS,
  OKTA_API_ERROR_CODE_SUMMARY_MAP,
} from "../lib/constants";
import { AxiosResponse, isAxiosError } from "axios";
import { AuthState } from "@okta/okta-auth-js";
import { ChangePasswordRequest } from "../types/ApiRequest.type";

export const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (error?.response && error.response.data) {
      if (error.response.data.errorSummary) {
        if (
          error.response.data.errorCauses &&
          error.response.data.errorCauses.length > 0
        ) {
          return error.response?.data?.errorCauses[0].errorSummary;
        }
        return error.response.data.errorSummary;
      }
      if (error.response.data.errorMessage) {
        return error.response.data.errorMessage;
      }
    }
    if (
      !error.response &&
      error.code &&
      NETWORK_RETRY_ERRORS.includes(error.code)
    ) {
      return error.message || "Network error";
    }
    return "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
};

export const getErrorCode = (error: unknown): string => {
  if (isAxiosError(error)) {
    if (error?.response && error.response.data) {
      if (error.response.data.errorCode) {
        return error.response.data.errorCode;
      }
    }
    if (!error.response && error.code) {
      return error.code;
    }
  }
  return "";
};

export const getAllUserGrants = async (): Promise<GetUserGrantsProps> => {
  let tempData: GetUserGrantsProps = [];
  let nextUrl: string | undefined =
    `${config.oktaURL}api/v1/users/me/grants?limit=${GET_GRANTS_API_LIMIT}`;

  do {
    const apiResponse: AxiosResponse<GetUserGrantsProps> = await callApi(
      nextUrl,
      "GET",
    );
    tempData = tempData.concat(apiResponse.data);

    const linkHeader = apiResponse.headers.link;
    const next = linkHeader ? parse(linkHeader) : null;

    nextUrl = next && next.next ? next.next.url : undefined;
  } while (nextUrl);

  return tempData;
};

export const getUserGrants = async (): Promise<GetUserGrantsProps> => {
  try {
    let response: GetUserGrantsProps = [];
    response = await getAllUserGrants();
    return response;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteUserGrants = async (args: {
  grantIds: string[];
}): Promise<boolean> => {
  args = { ...args };
  try {
    for (const grantId of args.grantIds) {
      const endpoint = `${config.oktaURL}api/v1/users/me/grants/${grantId}`;
      await callApi(endpoint, "DELETE");
    }
    return true;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 404) {
          return true;
        }
      }
    }
    throw new Error(getErrorMessage(error));
  }
};

export const deleteAllUserGrants = async (): Promise<boolean> => {
  try {
    const endpoint = `${config.oktaURL}/api/v1/users/me/grants`;
    await callApi(endpoint, "DELETE");
    return true;
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateMfaPromptEveryLogin = async (
  authState: AuthState | null,
  mfaPromptEveryLogin: boolean,
): Promise<UpdateMfaPromptResponse> => {
  try {
    const endpoint = `${config.iam.identityServiceUrl}/userprofile/mfaPromptEveryLogin`;
    const authorizationHeader = `Bearer ${authState?.accessToken?.accessToken}`;

    const payload = {
      mfaPromptEveryLogin,
    };

    const response = await callApi<typeof payload, UpdateMfaPromptResponse>(
      endpoint,
      "POST",
      payload,
      undefined,
      authorizationHeader,
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error(getErrorMessage(response));
    }
  } catch (error: unknown) {
    throw new Error(getErrorMessage(error));
  }
};

export const getEnrolledFactors = async (
  authState: AuthState | null,
): Promise<MFAEnrolledFactorAPI[]> => {
  if (authState?.accessToken?.accessToken) {
    const endpoint = `${config.iam.identityServiceUrl}/userprofile/factors`;
    const authorizationHeader = `Bearer ${authState?.accessToken?.accessToken}`;
    const res = await callApi(endpoint, "GET", undefined, undefined, authorizationHeader);
    return res.data as MFAEnrolledFactorAPI[];
  }
  return [];
};

export const getAvailableFactorsAndAuthenticatorInfo = async (
  authState: AuthState | null,
): Promise<MFAAvailableFactorAPI> => {
  if (authState?.accessToken?.accessToken) {
    const endpoint = `${config.iam.identityServiceUrl}/userprofile/factors/catalog`;
    const authorizationHeader = `Bearer ${authState?.accessToken?.accessToken}`;
    const res = await callApi(endpoint, "GET", undefined, undefined, authorizationHeader);
    return res.data as MFAAvailableFactorAPI;
  }
  return {} as MFAAvailableFactorAPI;
};

export const removeEnrolledFactor = async (
  factorId: string,
  authState: AuthState | null,
): Promise<void> => {
  if (authState?.accessToken?.accessToken) {
    const endpoint = `${config.iam.identityServiceUrl}/userprofile/factors/${factorId}`;
    const authorizationHeader = `Bearer ${authState?.accessToken?.accessToken}`;
    await callApi(endpoint, "DELETE", undefined, undefined, authorizationHeader);
  }
};

export const changePassword = async (
  authState: AuthState | null,
  changePasswordReqArgs: ChangePasswordRequest,
): Promise<ChangePasswordResponse> => {
  try {
    const endpoint = `${config.iam.identityServiceUrl}/userprofile/credentials/change_password`;
    const authorizationHeader = `Bearer ${authState?.accessToken?.accessToken}`;
    const res = await callApi(
      endpoint,
      "POST",
      changePasswordReqArgs,
      undefined,
      authorizationHeader,
    );
    return {
      status: res.status,
    };
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    const errorCode = getErrorCode(error);
    if (
      errorCode &&
      errorMessage &&
      errorCode in OKTA_API_ERROR_CODE_SUMMARY_MAP &&
      errorMessage in OKTA_API_ERROR_CODE_SUMMARY_MAP[errorCode]
    ) {
      const i18nErrorKeyAndContext =
        OKTA_API_ERROR_CODE_SUMMARY_MAP[errorCode][errorMessage];
      if (i18nErrorKeyAndContext) {
        return {
          status: 403,
          errorKey: i18nErrorKeyAndContext.i18nkey,
          inputContext: i18nErrorKeyAndContext.inputContext,
        };
      }
    }
    throw new Error(errorMessage);
  }
};

/**
 * sessionExists
 *
 * Purpose:
 * - Mimic the behavior of oktaAuth.session.exists() using a direct REST call to Okta's Sessions API.
 * - This checks whether there is a valid Okta browser session by attempting to fetch the current session
 *   using the `sessions/me` endpoint. If an Okta session cookie is present and valid, Okta will return 200
 *   with session details. If there is no session, Okta responds with 404. Any other network/HTTP errors
 *   should not throw from this helper and should be treated as "no active session" to match
 *   oktaAuth.session.exists() semantics.
 *
 * Important implementation details:
 * - We rely on `callApi` without an Authorization header so that `withCredentials` is set to true (see
 *   `src/api/CallApi.ts`), ensuring the Okta session cookie (if any) is sent cross-site.
 * - We intentionally swallow errors and return `false` for non-200 responses (including 404, 5xx, and
 *   network errors) to mirror the Promise<boolean> behavior of oktaAuth.session.exists().
 * - The endpoint is constructed from `config.oktaURL` (which is expected to include a trailing slash)
 *   and we append `api/v1/sessions/me` without a leading slash to avoid `//` in the URL.
 */
export const sessionExists = async (): Promise<boolean> => {
  // Compose the endpoint using the Okta base URL from configuration.
  const endpoint = `${config.oktaURL}api/v1/sessions/me`;

  try {
    // No Authorization header is provided here by design. `callApi` will set `withCredentials: true`
    // so that the Okta session cookie (if it exists) is included with the request.
    const res = await callApi(endpoint, "GET");

    // Okta returns HTTP 200 when a session exists. Any other status should be considered as no session.
    return res.status === 200;
  } catch (error) {
    // If Okta returns 404, it means there is no active session for the browser.
    // Network errors or 5xx from the server should also be treated as "no session" to match
    // oktaAuth.session.exists() behavior.
    if (isAxiosError(error) && error.response?.status === 404) {
      return false;
    }
    return false;
  }
};
